import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { useApp } from './AppContext';

// Initial state
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  role: null,
  permissions: []
};

// Action types
const actionTypes = {
  AUTH_START: 'AUTH_START',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  AUTH_FAILURE: 'AUTH_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  SET_LOADING: 'SET_LOADING'
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.AUTH_START:
      return {
        ...state,
        isLoading: true
      };

    case actionTypes.AUTH_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        role: action.payload.user.role,
        permissions: action.payload.user.permissions || []
      };

    case actionTypes.AUTH_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        role: null,
        permissions: []
      };

    case actionTypes.LOGOUT:
      return {
        ...initialState,
        isLoading: false
      };

    case actionTypes.UPDATE_USER:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload
        }
      };

    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { addNotification } = useApp();

  // Check for existing token on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('pizzainn_token');
      const user = localStorage.getItem('pizzainn_user');

      if (token && user) {
        try {
          // Verify token is still valid
          const userData = JSON.parse(user);
          const isValidToken = await authService.verifyToken(token);
          
          if (isValidToken) {
            dispatch({
              type: actionTypes.AUTH_SUCCESS,
              payload: {
                user: userData,
                token: token
              }
            });
          } else {
            // Token is invalid, clear storage
            logout();
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          logout();
        }
      } else {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      dispatch({ type: actionTypes.AUTH_START });

      const response = await authService.login(credentials);
      
      if (response.success) {
        const { user, token } = response.data;

        // Store in localStorage
        localStorage.setItem('pizzainn_token', token);
        localStorage.setItem('pizzainn_user', JSON.stringify(user));

        dispatch({
          type: actionTypes.AUTH_SUCCESS,
          payload: { user, token }
        });

        addNotification({
          type: 'success',
          message: `Welcome back, ${user.name}!`
        });

        return { success: true, user };
      } else {
        dispatch({ type: actionTypes.AUTH_FAILURE });
        addNotification({
          type: 'error',
          message: response.message || 'Login failed'
        });
        return { success: false, message: response.message };
      }
    } catch (error) {
      dispatch({ type: actionTypes.AUTH_FAILURE });
      addNotification({
        type: 'error',
        message: error.message || 'Login failed'
      });
      return { success: false, message: error.message };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: actionTypes.AUTH_START });

      const response = await authService.register(userData);
      
      if (response.success) {
        const { user, token } = response.data;

        // Store in localStorage
        localStorage.setItem('pizzainn_token', token);
        localStorage.setItem('pizzainn_user', JSON.stringify(user));

        dispatch({
          type: actionTypes.AUTH_SUCCESS,
          payload: { user, token }
        });

        addNotification({
          type: 'success',
          message: `Welcome to Pizza Inn, ${user.name}!`
        });

        return { success: true, user };
      } else {
        dispatch({ type: actionTypes.AUTH_FAILURE });
        addNotification({
          type: 'error',
          message: response.message || 'Registration failed'
        });
        return { success: false, message: response.message };
      }
    } catch (error) {
      dispatch({ type: actionTypes.AUTH_FAILURE });
      addNotification({
        type: 'error',
        message: error.message || 'Registration failed'
      });
      return { success: false, message: error.message };
    }
  };

  // Logout function
  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('pizzainn_token');
    localStorage.removeItem('pizzainn_user');

    dispatch({ type: actionTypes.LOGOUT });

    addNotification({
      type: 'success',
      message: 'You have been logged out successfully'
    });
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const response = await authService.updateProfile(userData);
      
      if (response.success) {
        const updatedUser = response.data.user;
        
        // Update localStorage
        localStorage.setItem('pizzainn_user', JSON.stringify(updatedUser));

        dispatch({
          type: actionTypes.UPDATE_USER,
          payload: updatedUser
        });

        addNotification({
          type: 'success',
          message: 'Profile updated successfully'
        });

        return { success: true, user: updatedUser };
      } else {
        addNotification({
          type: 'error',
          message: response.message || 'Profile update failed'
        });
        return { success: false, message: response.message };
      }
    } catch (error) {
      addNotification({
        type: 'error',
        message: error.message || 'Profile update failed'
      });
      return { success: false, message: error.message };
    }
  };

  // Change password
  const changePassword = async (passwordData) => {
    try {
      const response = await authService.changePassword(passwordData);
      
      if (response.success) {
        addNotification({
          type: 'success',
          message: 'Password changed successfully'
        });
        return { success: true };
      } else {
        addNotification({
          type: 'error',
          message: response.message || 'Password change failed'
        });
        return { success: false, message: response.message };
      }
    } catch (error) {
      addNotification({
        type: 'error',
        message: error.message || 'Password change failed'
      });
      return { success: false, message: error.message };
    }
  };

  // Check if user has specific role
  const hasRole = (requiredRole) => {
    if (!state.user || !state.role) return false;
    
    const roleHierarchy = {
      'CUSTOMER': 1,
      'STAFF': 2,
      'MANAGER': 3,
      'ADMIN': 4,
      'SUPER_ADMIN': 5
    };

    const userRoleLevel = roleHierarchy[state.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;

    return userRoleLevel >= requiredRoleLevel;
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!state.permissions) return false;
    return state.permissions.includes(permission);
  };

  // Check if user can access admin panel
  const canAccessAdmin = () => {
    return hasRole('STAFF');
  };

  const value = {
    // State
    ...state,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    
    // Utilities
    hasRole,
    hasPermission,
    canAccessAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
